export enum TrackType {
  AUDIO = 'audio',
  TEXT = 'text',
  VIDEO = 'video',
}

export enum TrackKind {
  SUBTITLE = 'subtitle',
  CAPTION = 'caption',
}

export enum TrackRole {
  NONE = '',
  MAIN = 'main',
  DASH_AUDIO_DESCRIPTION = 'description',
  HLS_AUDIO_DESCRIPTION = 'public.accessibility.describes-video',
}

/**
 * @typedef {{
 *   type: string,
 *   language: string,
 * }}
 *
 * @description
 * An object describing a media track.  This object should be treated as
 * read-only as changing any values does not have any effect.  This is the
 * public view of an audio/video paring (variant type) or text track (text
 * type).
 *
 * @property {string} type
 *   The type of track, either <code>'Audio'</code> or <code>'text'</code>.
 * @property {string} language
 *   The language of the track, or <code>'und'</code> if not given.  This is the
 *   exact value provided in the manifest; it may need to be normalized.
 * @property {string} kind
 *   The kind of text track, either <code>'subtitle'</code> or <code>'caption'</code>.
 *   applicable only for <code>'text'</code> tracks.
 * @property {string} accessibilityPurpose
 *   The accessibility purpose of the track, if any.  This is a single value
 *   from the <code>accessibilityPurpose</code> field in the DASH spec.
 * @property {string} role
 *   The role of the track, if any.  This is a single value from the
 *   <code>role</code> field in the DASH spec.
 * @property {string|null} label
 *   The label of the track, if any.  This is often a human-readable
 *   description of the track's content.
 */

export interface TrackInfo {
  type: TrackType;
  language: string;
  originalLanguage?: string;
  kind?: TrackKind;
  accessibilityPurpose?: string;
  role?: string;
  label?: string | null;
  roles?: string[];
  active?: boolean;
  audioSamplingRate?: string | null;
  channelsCount?: number | null;
  codecs?: string;
  mimeType?: string;
  primary?: boolean;
  spatialAudio?: boolean;
  bandwidth?: number;
  forced?: boolean;
  id?: number;
  hdr?: string;
  originalTextId?: string;
  originalAudioId?: string;
}
